import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import {
  Conversation,
  ConversationSnapshotPage,
  ConversationSyncCursor,
  ConversationSyncPage,
  getConversationActivityTimestamp,
} from '@/models/conversation'
import { Client } from '@/models/client'
import { Contact } from '@/models/contact'
import { Message } from '@/models/message'
import { User } from '@/models/user'
import api from '@/shared/api'
import { canUserAccessConversation } from '@/shared/conversationAccess'
import {
  CONVERSATION_STATUS,
  ENDPOINTS,
  MESSAGE_STATUS,
  MESSAGE_TYPES,
  UTILS,
} from '@/shared/constants'
import { messageMatchesId, messagesMatch } from '@/shared/messageIdentity'
import { areStructurallyEqual } from '@/shared/structuralEquality'
import { RootState } from '..'

const conversationsAdapter = createEntityAdapter<Conversation>({
  sortComparer: (a, b) =>
    getConversationActivityTimestamp(b) - getConversationActivityTimestamp(a),
})

type ConversationsState = ReturnType<
  typeof conversationsAdapter.getInitialState
> & {
  isSelectActive: boolean
  selectedIds: number[]
  isLoading: boolean
  unreadMessages: { [conversaionId: number]: number }
  loadedConversations: { [conversaionId: number]: number }
  totalUnread: number
  pinnedConversationIds: number[]
  openedConversation?: Conversation
  openedConversationMessages: Message[]
  filteredConversations: Conversation[]
  clients: { [clientId: number]: Client }
  clientsStatusById: {
    [clientId: number]: 'idle' | 'loading' | 'succeeded' | 'failed'
  }
  searchedMessageId?: string
  searchedMessageConversationId?: number
  fetchRequestId?: string
  syncCursor?: ConversationSyncCursor
  availableSyncCursor?: ConversationSyncCursor
  syncRequestId?: string
  syncResetRequired: boolean
}

const initialState: ConversationsState = {
  ...conversationsAdapter.getInitialState(),
  selectedIds: [],
  isSelectActive: false,
  isLoading: true,
  unreadMessages: {},
  loadedConversations: {},
  totalUnread: 0,
  pinnedConversationIds: [],
  openedConversation: undefined,
  openedConversationMessages: [],
  filteredConversations: [],
  clients: [],
  clientsStatusById: {},
  searchedMessageId: undefined,
  searchedMessageConversationId: undefined,
  fetchRequestId: undefined,
  syncCursor: undefined,
  availableSyncCursor: undefined,
  syncRequestId: undefined,
  syncResetRequired: false,
}

function compareSequence(left: string, right: string): number {
  const normalizedLeft = left.replace(/^0+(?=\d)/, UTILS.BLANK)
  const normalizedRight = right.replace(/^0+(?=\d)/, UTILS.BLANK)

  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length - normalizedRight.length
  }

  return normalizedLeft.localeCompare(normalizedRight)
}

function updateTotalUnread(unreadMessages: {
  [conversaionId: number]: number
}) {
  return Array.from(Object.values(unreadMessages)).reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  )
}

function mergeMessagePreservingExisting(
  currentMessage: Message,
  incomingMessage: Message
): Message {
  let mergedConversation = currentMessage.conversation

  if (incomingMessage.conversation) {
    mergedConversation = {
      ...currentMessage.conversation,
      ...incomingMessage.conversation,
      contact:
        incomingMessage.conversation.contact ??
        currentMessage.conversation?.contact,
    }
  }

  const currentParent = currentMessage.parentMessage
  const incomingParent = incomingMessage.parentMessage
  const shouldKeepCurrentParentContent =
    currentParent?.type === MESSAGE_TYPES.FILE &&
    incomingParent?.type === MESSAGE_TYPES.FILE &&
    Boolean(currentParent.content) &&
    (!incomingParent.content || incomingParent.content.includes('whatsapp.net'))
  const nextContent = incomingMessage.content ?? currentMessage.content
  const shouldKeepCurrentLinkPreview =
    incomingMessage.linkPreview === undefined ||
    (incomingMessage.linkPreview === null &&
      Boolean(currentMessage.linkPreview) &&
      nextContent === currentMessage.content)

  const mergedMessage = {
    ...currentMessage,
    ...incomingMessage,
    user: incomingMessage.user ?? currentMessage.user,
    conversation: mergedConversation,
    parentMessage: incomingParent
      ? {
          ...currentParent,
          ...incomingParent,
          content: shouldKeepCurrentParentContent
            ? currentParent?.content
            : (incomingParent.content ?? currentParent?.content),
        }
      : currentParent,
    content: nextContent,
    linkPreview: shouldKeepCurrentLinkPreview
      ? currentMessage.linkPreview
      : incomingMessage.linkPreview,
  }

  return areStructurallyEqual(currentMessage, mergedMessage)
    ? currentMessage
    : mergedMessage
}

function updateMessage(messages: Message[], message: Message) {
  const index = messages.findIndex((currentMessage) =>
    messagesMatch(currentMessage, message)
  )
  if (index !== -1) {
    messages[index] = mergeMessagePreservingExisting(messages[index], message)
  }
}

function findMessageIndex(messages: Message[], message: Message) {
  return messages.findIndex((currentMessage) =>
    messagesMatch(currentMessage, message)
  )
}

function mergeConversationMessages(
  existingMessages: Message[] = [],
  incomingMessages: Message[] = []
) {
  const mergedMessages = [...existingMessages]
  let hasChanges = false

  incomingMessages.forEach((incomingMessage) => {
    const index = findMessageIndex(mergedMessages, incomingMessage)

    if (index === -1) {
      mergedMessages.push(incomingMessage)
      hasChanges = true
      return
    }

    const mergedMessage = mergeMessagePreservingExisting(
      mergedMessages[index],
      incomingMessage
    )

    if (mergedMessage !== mergedMessages[index]) {
      mergedMessages[index] = mergedMessage
      hasChanges = true
    }
  })

  if (!hasChanges) return existingMessages

  return mergedMessages.sort(
    (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
  )
}

function isHttpUrl(path?: string) {
  return Boolean(path?.trim().match(/^https?:\/\//i))
}

function mergeConversationContact(
  currentConversation: Conversation | undefined,
  incomingConversation: Conversation
): Contact | undefined {
  if (!incomingConversation.contact) {
    return currentConversation?.contact
  }

  const currentPath = currentConversation?.contact?.path ?? UTILS.BLANK
  const incomingPath = incomingConversation.contact.path?.trim()
  const shouldPreserveCurrentPath =
    Boolean(currentPath) &&
    isHttpUrl(currentPath) &&
    (!incomingPath || !isHttpUrl(incomingPath))

  return {
    ...currentConversation?.contact,
    ...incomingConversation.contact,
    path: shouldPreserveCurrentPath
      ? currentPath
      : incomingPath || currentPath || incomingConversation.contact.path,
  }
}

function shouldAddConversation(
  state: typeof initialState,
  conversation: Conversation,
  user: User
) {
  const exists = state.entities[conversation.id]
  if (exists) return false

  return canUserAccessConversation(conversation, user)
}

function isOpportunityConversation(conversation: Conversation) {
  return (
    conversation.routingArea === 'opportunities' ||
    Boolean(conversation.opportunityId)
  )
}

function addConversationLogic(
  state: typeof initialState,
  conversation: Conversation,
  user: User
) {
  if (isOpportunityConversation(conversation)) return
  const validation = shouldAddConversation(state, conversation, user)
  if (validation) {
    conversationsAdapter.addOne(state, conversation)
    state.unreadMessages[conversation.id] = conversation.unreadCount
    state.totalUnread += conversation.unreadCount
  }
}

function addMessageToOpenedConversationMessagesLogic(
  state: typeof initialState,
  message: Message
) {
  if (state.openedConversation?.id !== message.conversationId) return

  const alreadyExists = state.openedConversationMessages.some(
    (currentMessage) => messagesMatch(currentMessage, message)
  )
  if (!alreadyExists) {
    state.openedConversationMessages.push(message)
  } else {
    updateMessage(state.openedConversationMessages, message)
  }
}

export type ConversationFetchReason =
  | 'initial'
  | 'periodic'
  | 'focus'
  | 'visibility'
  | 'socket_reconnect'
  | 'socket_refresh'
  | 'client_removal'
  | 'contact_transfer'
  | 'incremental_reset'

type FetchConversationsArgs = {
  reason: ConversationFetchReason
  silent?: boolean
  replace?: boolean
}

const activeConversationSnapshotStatuses = [
  CONVERSATION_STATUS.WAITING,
  CONVERSATION_STATUS.IN_SERVICE,
]
const conversationSnapshotPageSize = 50
const conversationSyncPageSize = 200
interface ConversationSnapshotResult {
  conversations: Conversation[]
  syncCursor?: string
}

interface FetchConversationsResult extends ConversationSnapshotResult {
  scope?: Pick<ConversationSyncCursor, 'companyId' | 'userId'>
}

async function fetchConversationSnapshotV2(
  reason: ConversationFetchReason,
  signal?: AbortSignal
): Promise<ConversationSnapshotResult> {
  const pages = await Promise.all(
    activeConversationSnapshotStatuses.map(async (status) => {
      const items: Conversation[] = []
      const visitedCursors = new Set<string>()
      let syncCursor: string | null = null
      let cursor: string | undefined

      do {
        const response = await api.get<ConversationSnapshotPage>(
          ENDPOINTS.CONVERSATIONS.GET.SNAPSHOT_V2,
          {
            headers: {
              'x-conversations-fetch-reason': reason,
            },
            params: {
              status,
              limit: conversationSnapshotPageSize,
              ...(cursor ? { cursor } : {}),
            },
            signal,
          }
        )
        items.push(...response.data.items)
        syncCursor ??= response.data.syncCursor

        if (!response.data.hasMore) break
        if (
          !response.data.nextCursor ||
          visitedCursors.has(response.data.nextCursor)
        ) {
          throw new Error('Invalid conversation snapshot pagination')
        }

        visitedCursors.add(response.data.nextCursor)
        cursor = response.data.nextCursor
      } while (cursor)

      return { items, syncCursor }
    })
  )

  const syncCursor = pages
    .map((page) => page.syncCursor)
    .filter((value): value is string => Boolean(value))
    .reduce<string | undefined>(
      (lowest, value) =>
        lowest === undefined || compareSequence(value, lowest) < 0
          ? value
          : lowest,
      undefined
    )

  return {
    conversations: pages.flatMap((page) => page.items),
    syncCursor,
  }
}

export const fetchConversations = createAsyncThunk<
  FetchConversationsResult,
  FetchConversationsArgs,
  { state: RootState }
>('conversations/fetchConversations', async (args, thunkAPI) => {
  try {
    const user = thunkAPI.getState().auth.user
    const scope =
      user?.companyId != null
        ? { companyId: user.companyId, userId: user.id }
        : undefined

    const snapshot = await fetchConversationSnapshotV2(
      args.reason,
      thunkAPI.signal
    )

    return { ...snapshot, scope }
  } catch (error: unknown) {
    if (!args?.silent) {
      toast.error('Erro ao listar atendimentos')
    }
    let errorMessage = 'Unknown error'

    if (error instanceof Error) {
      errorMessage = error.message
    }

    return thunkAPI.rejectWithValue(errorMessage)
  }
})

interface SyncConversationsResult {
  upserts: Conversation[]
  removedIds: number[]
  cursor: string
  resetRequired: boolean
  scope: Pick<ConversationSyncCursor, 'companyId' | 'userId'>
  fromCursor: string
}

export const syncConversations = createAsyncThunk<
  SyncConversationsResult,
  void,
  { state: RootState }
>(
  'conversations/syncConversations',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState()
    const user = state.auth.user
    const syncCursor = state.conversations.syncCursor

    if (
      !user?.companyId ||
      !syncCursor ||
      syncCursor.companyId !== user.companyId ||
      syncCursor.userId !== user.id
    ) {
      throw new Error('Conversation sync cursor is not available')
    }

    const scope = { companyId: user.companyId, userId: user.id }
    const upserts = new Map<number, Conversation>()
    const removedIds = new Set<number>()
    const visitedCursors = new Set<string>([syncCursor.value])
    let cursor = syncCursor.value

    do {
      const response = await api.get<ConversationSyncPage>(
        ENDPOINTS.CONVERSATIONS.GET.SYNC,
        {
          headers: {
            'x-conversations-fetch-reason': 'incremental_sync',
          },
          params: { after: cursor, limit: conversationSyncPageSize },
          signal: thunkAPI.signal,
        }
      )
      const page = response.data

      if (page.resetRequired) {
        return {
          upserts: [],
          removedIds: [],
          cursor: syncCursor.value,
          resetRequired: true,
          scope,
          fromCursor: syncCursor.value,
        }
      }

      page.upserts.forEach((conversation) => {
        removedIds.delete(conversation.id)
        upserts.set(conversation.id, conversation)
      })
      page.removedIds.forEach((id) => {
        upserts.delete(id)
        removedIds.add(id)
      })

      if (!page.hasMore) {
        cursor = page.cursor
        break
      }
      if (page.cursor === cursor || visitedCursors.has(page.cursor)) {
        throw new Error('Invalid conversation sync pagination')
      }

      visitedCursors.add(page.cursor)
      cursor = page.cursor
    } while (!thunkAPI.signal.aborted)

    if (thunkAPI.signal.aborted) {
      throw new Error('Conversation sync aborted')
    }

    return {
      upserts: [...upserts.values()],
      removedIds: [...removedIds],
      cursor,
      resetRequired: false,
      scope,
      fromCursor: syncCursor.value,
    }
  },
  {
    condition: (_, { getState }) => !getState().conversations.syncRequestId,
  }
)

type ReconcileConversationsArgs = {
  reason: Extract<
    ConversationFetchReason,
    | 'periodic'
    | 'focus'
    | 'visibility'
    | 'socket_reconnect'
    | 'socket_refresh'
  >
}

export const reconcileConversations = createAsyncThunk<
  void,
  ReconcileConversationsArgs,
  { state: RootState }
>('conversations/reconcileConversations', async ({ reason }, thunkAPI) => {
  const state = thunkAPI.getState()
  const user = state.auth.user
  const conversationsState = state.conversations

  if (!user?.companyId) return
  if (conversationsState.fetchRequestId || conversationsState.syncRequestId) {
    return
  }

  const syncCursor = conversationsState.syncCursor
  const hasCurrentCursor =
    syncCursor?.companyId === user.companyId && syncCursor.userId === user.id

  if (!hasCurrentCursor) {
    await thunkAPI
      .dispatch(
        fetchConversations({
          reason,
          silent: true,
          replace: true,
        })
      )
      .unwrap()
    return
  }

  const result = await thunkAPI.dispatch(syncConversations()).unwrap()
  if (!result.resetRequired) return

  await thunkAPI
    .dispatch(
      fetchConversations({
        reason: 'incremental_reset',
        silent: true,
        replace: true,
      })
    )
    .unwrap()
})

type FetchClientReject = string
type FetchClientArgs = {
  clientId: string
  force?: boolean
}

export const fetchClient = createAsyncThunk<
  Client,
  FetchClientArgs,
  { rejectValue: FetchClientReject; state: RootState }
>(
  'conversations/fetchClient',
  async ({ clientId }, thunkAPI) => {
    try {
      const response = await api.get(
        ENDPOINTS.CLIENTS.GET.BY_ID_FOR_CONVERSATION.replace(
          UTILS.PARAMS.ID,
          clientId
        )
      )
      return response.data as Client
    } catch (error: unknown) {
      toast.error('Erro ao obter cliente')
      const msg = error instanceof Error ? error.message : 'Unknown error'
      return thunkAPI.rejectWithValue(msg)
    }
  },
  {
    condition: ({ clientId, force }, { getState }) => {
      if (force) return true

      const state = getState()
      const id = clientId

      const status = state.conversations.clientsStatusById[+id]
      const alreadyHave = Boolean(state.conversations.clients[+id])

      if (alreadyHave) return false
      if (status === 'loading') return false
      if (status === 'succeeded') return false

      return true
    },
  }
)

export const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    observeConversationChangeSequence(
      state,
      action: PayloadAction<ConversationSyncCursor>
    ) {
      const current = state.availableSyncCursor
      if (
        !current ||
        current.companyId !== action.payload.companyId ||
        current.userId !== action.payload.userId ||
        compareSequence(action.payload.value, current.value) > 0
      ) {
        state.availableSyncCursor = action.payload
      }
    },
    setFilteredConversations: (
      state,
      action: PayloadAction<Conversation[]>
    ) => {
      state.filteredConversations = action.payload
    },
    setPinnedConversationIds: (state, action: PayloadAction<number[]>) => {
      state.pinnedConversationIds = action.payload
    },
    togglePinnedConversation: (state, action: PayloadAction<number>) => {
      const id = action.payload

      if (state.pinnedConversationIds.includes(id)) {
        state.pinnedConversationIds = state.pinnedConversationIds.filter(
          (pinnedId) => pinnedId !== id
        )
      } else {
        state.pinnedConversationIds.push(id)
      }
    },
    setIsSelectActive: (state, action: PayloadAction<boolean>) => {
      state.isSelectActive = action.payload
    },
    toggleSelect(state, action: PayloadAction<number>) {
      const id = action.payload
      if (state.selectedIds.includes(id)) {
        state.selectedIds = state.selectedIds.filter((selId) => selId !== id)
      } else {
        state.selectedIds.push(id)
      }
    },
    selectAll(state, action: PayloadAction<Conversation[]>) {
      state.selectedIds = action.payload.map((conversation) => conversation.id)
    },
    clearSelection(state) {
      state.selectedIds = []
    },
    setSearchedMessageId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.searchedMessageId = action.payload
      if (!action.payload) {
        state.searchedMessageConversationId = undefined
      }
    },
    setSearchedMessageNavigation: (
      state,
      action: PayloadAction<
        | {
            messageId: string
            conversationId: number
          }
        | undefined
      >
    ) => {
      state.searchedMessageId = action.payload?.messageId
      state.searchedMessageConversationId = action.payload?.conversationId
    },
    setConversationUnreadCount: (
      state,
      action: PayloadAction<{ id: number; unreadCount: number }>
    ) => {
      const { id, unreadCount } = action.payload
      state.unreadMessages[id] = unreadCount
      state.totalUnread = updateTotalUnread(state.unreadMessages)

      const conversation = state.entities[id]
      if (conversation) {
        conversation.unreadCount = unreadCount
      }

      if (state.openedConversation?.id === id) {
        state.openedConversation.unreadCount = unreadCount
      }
    },
    setOpenedConversation: (
      state,
      action: PayloadAction<Conversation | undefined>
    ) => {
      if (action.payload) {
        const incoming = action.payload
        const existing = state.entities[incoming.id]
        const currentOpenedConversation =
          state.openedConversation?.id === incoming.id
            ? state.openedConversation
            : undefined
        const contact =
          incoming.contact ??
          currentOpenedConversation?.contact ??
          existing?.contact
        const baseMessages = mergeConversationMessages(
          existing?.messages,
          currentOpenedConversation ? state.openedConversationMessages : []
        )
        const mergedMessages = mergeConversationMessages(
          baseMessages,
          incoming.messages
        )
        const stableMergedMessages = areStructurallyEqual(
          state.openedConversationMessages,
          mergedMessages
        )
          ? state.openedConversationMessages
          : mergedMessages
        const nextOpenedConversation = {
          ...incoming,
          contact,
          client:
            incoming.client ??
            currentOpenedConversation?.client ??
            existing?.client,
          messages: stableMergedMessages,
        }

        if (
          !currentOpenedConversation ||
          !areStructurallyEqual(
            currentOpenedConversation,
            nextOpenedConversation
          )
        ) {
          state.openedConversation = nextOpenedConversation
        }
        state.unreadMessages[incoming.id] = 0
        state.totalUnread = updateTotalUnread(state.unreadMessages)
        if (state.openedConversationMessages !== stableMergedMessages) {
          state.openedConversationMessages = stableMergedMessages
        }

        const nextConversation = {
          ...existing, // mantém o que já tinha (inclui client)
          ...incoming, // atualiza o que veio
          client: incoming.client ?? existing?.client, // garante preservação
          contact,
          messages: stableMergedMessages,
        }
        if (
          !isOpportunityConversation(nextConversation) &&
          (!existing || !areStructurallyEqual(existing, nextConversation))
        ) {
          conversationsAdapter.upsertOne(state, nextConversation)
        }
      } else {
        state.openedConversation = undefined
        state.openedConversationMessages = []
      }
    },
    addConversation: (
      state,
      action: PayloadAction<{ conversation: Conversation; user: User }>
    ) => {
      addConversationLogic(
        state,
        action.payload.conversation,
        action.payload.user
      )
    },
    updateConversation: (
      state,
      action: PayloadAction<{ conversation: Conversation; user: User }>
    ) => {
      const { conversation: incomingConversation, user } = action.payload
      const shouldRemoveByAccess =
        isOpportunityConversation(incomingConversation) ||
        !canUserAccessConversation(incomingConversation, user)
      if (shouldRemoveByAccess) {
        conversationsAdapter.removeOne(state, incomingConversation.id)
        delete state.unreadMessages[incomingConversation.id]
        state.totalUnread = updateTotalUnread(state.unreadMessages)
        state.selectedIds = state.selectedIds.filter(
          (selectedId) => selectedId !== incomingConversation.id
        )

        if (
          isOpportunityConversation(incomingConversation) &&
          state.openedConversation?.id === incomingConversation.id
        ) {
          state.openedConversation = {
            ...state.openedConversation,
            ...incomingConversation,
            client: incomingConversation.client ?? state.openedConversation.client,
            contact: mergeConversationContact(
              state.openedConversation,
              incomingConversation
            ) ?? state.openedConversation.contact,
          }
        } else if (state.openedConversation?.id === incomingConversation.id) {
          state.openedConversation = undefined
          state.openedConversationMessages = []
        }

        return
      }

      const currentConversation = conversationsAdapter
        .getSelectors()
        .selectById(state, incomingConversation.id)
      if (!currentConversation) {
        addConversationLogic(state, incomingConversation, user)
      }

      state.unreadMessages[incomingConversation.id] = 0
      state.totalUnread = updateTotalUnread(state.unreadMessages)

      if (state.openedConversation?.id === incomingConversation.id) {
        state.openedConversation.status = incomingConversation.status
      }

      conversationsAdapter.updateOne(state, {
        id: incomingConversation.id,
        changes: {
          ...incomingConversation,
          contact: mergeConversationContact(
            currentConversation,
            incomingConversation
          ),
          messages: mergeConversationMessages(
            currentConversation?.messages,
            incomingConversation.messages
          ),
        },
      })
    },
    syncClientIntoConversation(
      state,
      action: PayloadAction<{
        conversationId: number
        client: Client
      }>
    ) {
      const conversation = state.entities[action.payload.conversationId]
      if (!conversation) return

      conversation.client = action.payload.client
      state.clients[action.payload.client.id] = action.payload.client

      if (state.openedConversation?.id === action.payload.conversationId) {
        state.openedConversation.client = action.payload.client
      }
    },
    syncClientAcrossConversations(
      state,
      action: PayloadAction<{
        fromClientId: number
        toClient: Client
      }>
    ) {
      const { fromClientId, toClient } = action.payload

      state.clients[toClient.id] = toClient

      Object.values(state.entities).forEach((conversation) => {
        if (!conversation) return

        if (conversation.client?.id === fromClientId) {
          conversation.client = toClient
        }
      })

      if (
        state.openedConversation &&
        state.openedConversation.client?.id === fromClientId
      ) {
        state.openedConversation.client = toClient
      }
    },
    refreshConversationClients(state, action: PayloadAction<Client[]>) {
      const clients = new Map(
        action.payload.map((client) => [client.id, client])
      )
      for (const client of action.payload) {
        if (state.clients[client.id]) state.clients[client.id] = client
      }
      Object.values(state.entities).forEach((conversation) => {
        if (!conversation) return
        const client = clients.get(conversation.client?.id)
        if (client) conversation.client = client
      })
      if (state.openedConversation?.client) {
        const client = clients.get(state.openedConversation.client.id)
        if (client) state.openedConversation.client = client
      }
    },
    addMessageToConversation: (state, action: PayloadAction<Message>) => {
      const msg = action.payload
      const conv = state.entities[msg.conversationId]
      if (!conv) return

      conv.messages ??= []

      const idx = findMessageIndex(conv.messages, msg)

      if (idx === -1) conv.messages.push(msg)
      else
        conv.messages[idx] = mergeMessagePreservingExisting(
          conv.messages[idx],
          msg
        )

      if (state.openedConversation?.id === conv.id) {
        addMessageToOpenedConversationMessagesLogic(state, msg)
      } else if (msg.isReceived) {
        state.unreadMessages[conv.id] = (state.unreadMessages[conv.id] || 0) + 1
        state.totalUnread += 1
        conv.unreadCount = (conv.unreadCount || 0) + 1
      }

      conversationsAdapter.updateOne(state, {
        id: conv.id,
        changes: {
          messages: conv.messages,
          unreadCount: conv.unreadCount,
        },
      })
    },
    addOptimisticToOpened: (state, action: PayloadAction<Message>) => {
      const msg = action.payload

      addMessageToOpenedConversationMessagesLogic(state, msg)
    },
    removeOptimisticFromOpened: (
      state,
      action: PayloadAction<{ conversationId: number; clientMessageId: string }>
    ) => {
      if (state.openedConversation?.id !== action.payload.conversationId) return

      state.openedConversationMessages =
        state.openedConversationMessages.filter(
          (message) =>
            message.status !== MESSAGE_STATUS.PENDING ||
            !messageMatchesId(message, action.payload.clientMessageId)
        )
    },
    syncConversationPreviewMessage: (state, action: PayloadAction<Message>) => {
      const message = action.payload
      const conversation = state.entities[message.conversationId]

      if (conversation) {
        conversation.messages = mergeConversationMessages(
          conversation.messages,
          [message]
        )

        conversationsAdapter.updateOne(state, {
          id: conversation.id,
          changes: {
            messages: conversation.messages,
          },
        })
      }

      if (state.openedConversation?.id === message.conversationId) {
        state.openedConversation.messages = mergeConversationMessages(
          state.openedConversation.messages,
          [message]
        )
      }
    },
    updateMessageStatus(state, action: PayloadAction<Message>) {
      const existing = conversationsAdapter
        .getSelectors()
        .selectById(state, action.payload.conversationId)
      if (!existing) return

      const existingMessage = existing.messages?.find((message) =>
        messagesMatch(message, action.payload)
      )
      const nextFailureReason = action.payload.failureReason ?? null
      const nextReceivedAt =
        action.payload.receivedAt ?? existingMessage?.receivedAt
      const nextReadAt = action.payload.readAt ?? existingMessage?.readAt
      // Provedores podem repetir o mesmo evento de ciclo de vida. Preservar a
      // referência do estado no no-op evita notificar toda a árvore React.
      const shouldUpdateConversationMessage =
        !!existingMessage &&
        (existingMessage.status !== action.payload.status ||
          existingMessage.failureReason !== nextFailureReason ||
          existingMessage.receivedAt !== nextReceivedAt ||
          existingMessage.readAt !== nextReadAt)

      if (shouldUpdateConversationMessage) {
        const conversation = structuredClone(existing)
        const message = conversation.messages.find((currentMessage) =>
          messagesMatch(currentMessage, action.payload)
        )!
        message.status = action.payload.status
        message.failureReason = nextFailureReason
        message.receivedAt = nextReceivedAt
        message.readAt = nextReadAt

        conversationsAdapter.updateOne(state, {
          id: conversation.id,
          changes: conversation,
        })
      }

      if (existing.id === state.openedConversation?.id) {
        const messageToUpdate = state.openedConversationMessages.find(
          (message) => messagesMatch(message, action.payload)
        )
        if (
          messageToUpdate &&
          (messageToUpdate.status !== action.payload.status ||
            messageToUpdate.failureReason !== nextFailureReason ||
            messageToUpdate.receivedAt !==
              (action.payload.receivedAt ?? messageToUpdate.receivedAt) ||
            messageToUpdate.readAt !==
              (action.payload.readAt ?? messageToUpdate.readAt))
        ) {
          messageToUpdate.status = action.payload.status
          messageToUpdate.failureReason = nextFailureReason
          messageToUpdate.receivedAt =
            action.payload.receivedAt ?? messageToUpdate.receivedAt
          messageToUpdate.readAt =
            action.payload.readAt ?? messageToUpdate.readAt
        }
      }
    },
    updateMessageId(state, action: PayloadAction<Message>) {
      const existing = conversationsAdapter
        .getSelectors()
        .selectById(state, action.payload.conversationId)
      if (!existing) return

      const existingMessage = existing.messages?.find((message) =>
        messagesMatch(message, action.payload)
      )
      // A confirmação do messageId pode chegar duplicada pelo Socket.IO.
      const shouldUpdateConversationMessage =
        !!existingMessage &&
        (existingMessage.id !== action.payload.id ||
          existingMessage.messageId !== action.payload.messageId ||
          existingMessage.status !== action.payload.status ||
          existingMessage.sentAt !== action.payload.sentAt)

      if (shouldUpdateConversationMessage) {
        const conversation = structuredClone(existing)
        const message = conversation.messages.find((currentMessage) =>
          messagesMatch(currentMessage, action.payload)
        )!
        message.id = action.payload.id
        message.messageId = action.payload.messageId
        message.status = action.payload.status
        message.sentAt = action.payload.sentAt

        conversationsAdapter.updateOne(state, {
          id: conversation.id,
          changes: conversation,
        })
      }

      if (existing.id === state.openedConversation?.id) {
        const messageToUpdate = state.openedConversationMessages.find(
          (message) => messagesMatch(message, action.payload)
        )
        if (
          messageToUpdate &&
          (messageToUpdate.id !== action.payload.id ||
            messageToUpdate.messageId !== action.payload.messageId ||
            messageToUpdate.status !== action.payload.status ||
            messageToUpdate.sentAt !== action.payload.sentAt)
        ) {
          messageToUpdate.id = action.payload.id
          messageToUpdate.messageId = action.payload.messageId
          messageToUpdate.status = action.payload.status
          messageToUpdate.sentAt = action.payload.sentAt
        }
      }
    },
    addMessageToOpenedConversationMessages(
      state,
      action: PayloadAction<Message>
    ) {
      addMessageToOpenedConversationMessagesLogic(state, action.payload)
    },
    addLoadedConversation(
      state,
      action: PayloadAction<{ conversationId: number; loadedAt?: number }>
    ) {
      state.loadedConversations[action.payload.conversationId] =
        action.payload.loadedAt ?? Date.now()
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchClient.fulfilled,
        (state, action: PayloadAction<Client>) => {
          state.clients[action.payload.id] = action.payload
          state.clientsStatusById[action.payload.id] = 'succeeded'
        }
      )
      .addCase(fetchClient.pending, (state, action) => {
        const { clientId } = action.meta.arg
        state.clientsStatusById[+clientId] = 'loading'
      })
      .addCase(fetchClient.rejected, (state, action) => {
        const { clientId } = action.meta.arg
        state.clientsStatusById[+clientId] = 'failed'
      })
      .addCase(fetchConversations.pending, (state, action) => {
        state.fetchRequestId = action.meta.requestId
        if (!action.meta.arg?.silent) {
          state.isLoading = true
        }
      })
      .addCase(
        fetchConversations.fulfilled,
        (state, action) => {
          if (state.fetchRequestId !== action.meta.requestId) return

          state.fetchRequestId = undefined
          state.isLoading = false
          const conversations = action.payload.conversations
            .filter((incoming) => !isOpportunityConversation(incoming))
            .map((incoming) => {
              const existing = state.entities[incoming.id]

              return {
                ...incoming,
                messages: mergeConversationMessages(
                  existing?.messages,
                  incoming.messages
                ),
              }
            })

          if (action.meta.arg?.replace) {
            const isInitialSnapshot = state.ids.length === 0
            const visibleConversationIds = new Set(
              state.filteredConversations.map((conversation) => conversation.id)
            )
            const conversationIds = new Set(
              conversations.map((conversation) => conversation.id)
            )
            const retainedOpenedConversation =
              state.openedConversation &&
              !isOpportunityConversation(state.openedConversation) &&
              !conversationIds.has(state.openedConversation.id)
                ? [state.openedConversation]
                : []

            conversationsAdapter.setAll(state, [
              ...conversations,
              ...retainedOpenedConversation,
            ])
            state.selectedIds = state.selectedIds.filter((id) =>
              conversationIds.has(id)
            )
            state.filteredConversations = isInitialSnapshot
              ? conversations
              : conversations.filter((conversation) =>
                  visibleConversationIds.has(conversation.id)
                )
          } else {
            conversationsAdapter.upsertMany(state, conversations)
          }
          state.totalUnread = 0
          state.unreadMessages = {}
          conversations.forEach((item) => {
            if (
              item.status === CONVERSATION_STATUS.WAITING ||
              item.status === CONVERSATION_STATUS.IN_SERVICE
            ) {
              state.unreadMessages[item.id] = item.unreadCount
              state.totalUnread += item.unreadCount
            }
          })

          if (action.payload.syncCursor && action.payload.scope) {
            const nextCursor = {
              ...action.payload.scope,
              value: action.payload.syncCursor,
            }
            const scopeChanged =
              state.syncCursor?.companyId !== nextCursor.companyId ||
              state.syncCursor?.userId !== nextCursor.userId

            state.syncCursor = nextCursor
            state.syncResetRequired = false
            if (
              scopeChanged ||
              !state.availableSyncCursor ||
              compareSequence(
                nextCursor.value,
                state.availableSyncCursor.value
              ) > 0
            ) {
              state.availableSyncCursor = nextCursor
            }
          }
        }
      )
      .addCase(fetchConversations.rejected, (state, action) => {
        if (state.fetchRequestId !== action.meta.requestId) return

        state.fetchRequestId = undefined
        state.isLoading = false
      })
      .addCase(syncConversations.pending, (state, action) => {
        state.syncRequestId = action.meta.requestId
      })
      .addCase(syncConversations.fulfilled, (state, action) => {
        if (state.syncRequestId !== action.meta.requestId) return

        state.syncRequestId = undefined
        if (action.payload.resetRequired) {
          state.syncResetRequired = true
          return
        }

        if (
          state.syncCursor?.companyId !== action.payload.scope.companyId ||
          state.syncCursor?.userId !== action.payload.scope.userId ||
          state.syncCursor.value !== action.payload.fromCursor
        ) {
          return
        }

        const opportunityConversationIds = action.payload.upserts
          .filter(isOpportunityConversation)
          .map(({ id }) => id)
        const upserts = action.payload.upserts
          .filter((incoming) => !isOpportunityConversation(incoming))
          .map((incoming) => {
          const existing = state.entities[incoming.id]

          return {
            ...incoming,
            messages: mergeConversationMessages(
              existing?.messages,
              incoming.messages
            ),
          }
          })

        conversationsAdapter.upsertMany(state, upserts)
        const removedConversationIds = [
          ...action.payload.removedIds,
          ...opportunityConversationIds,
        ]
        conversationsAdapter.removeMany(state, removedConversationIds)

        const removedIds = new Set(removedConversationIds)
        state.selectedIds = state.selectedIds.filter(
          (id) => !removedIds.has(id)
        )
        state.filteredConversations = state.filteredConversations.filter(
          ({ id }) => !removedIds.has(id)
        )
        if (
          state.openedConversation &&
          removedIds.has(state.openedConversation.id) &&
          !isOpportunityConversation(state.openedConversation)
        ) {
          state.openedConversation = undefined
          state.openedConversationMessages = []
        }

        action.payload.removedIds.forEach((id) => {
          delete state.unreadMessages[id]
        })
        upserts.forEach((conversation) => {
          if (
            conversation.status === CONVERSATION_STATUS.WAITING ||
            conversation.status === CONVERSATION_STATUS.IN_SERVICE
          ) {
            state.unreadMessages[conversation.id] = conversation.unreadCount
          } else {
            delete state.unreadMessages[conversation.id]
          }
        })
        state.totalUnread = updateTotalUnread(state.unreadMessages)

        const nextCursor = {
          ...action.payload.scope,
          value: action.payload.cursor,
        }
        state.syncCursor = nextCursor
        state.syncResetRequired = false
        if (
          !state.availableSyncCursor ||
          state.availableSyncCursor.companyId !== nextCursor.companyId ||
          state.availableSyncCursor.userId !== nextCursor.userId ||
          compareSequence(
            nextCursor.value,
            state.availableSyncCursor.value
          ) > 0
        ) {
          state.availableSyncCursor = nextCursor
        }
      })
      .addCase(syncConversations.rejected, (state, action) => {
        if (state.syncRequestId !== action.meta.requestId) return

        state.syncRequestId = undefined
      })
  },
})

export const {
  observeConversationChangeSequence,
  setFilteredConversations,
  setPinnedConversationIds,
  togglePinnedConversation,
  toggleSelect,
  selectAll,
  clearSelection,
  setIsSelectActive,
  setOpenedConversation,
  setConversationUnreadCount,
  setSearchedMessageId,
  setSearchedMessageNavigation,
  addConversation,
  updateConversation,
  syncClientIntoConversation,
  syncClientAcrossConversations,
  refreshConversationClients,
  addMessageToConversation,
  updateMessageStatus,
  updateMessageId,
  addMessageToOpenedConversationMessages,
  addLoadedConversation,
  addOptimisticToOpened,
  removeOptimisticFromOpened,
  syncConversationPreviewMessage,
} = conversationsSlice.actions

export default conversationsSlice.reducer

export const {
  selectAll: selectAllConversations,
  selectById: selectConversationById,
  selectIds: selectConversationIds,
} = conversationsAdapter.getSelectors((state: RootState) => state.conversations)

export const isConversationSelected = (id: number) =>
  createSelector(
    (state: RootState) => state.conversations.selectedIds,
    (selectedIds) => selectedIds.includes(id)
  )
